import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../data/types';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { ScreenStatGrid, ScreenMiniBars, ScreenDonut } from '../primitives/ChartPrimitives';
import { colors } from '../data/designTokens';

const statusTone = (status: string) => (status === 'OPEN' ? 'HIGH' : 'LOW');

const ScreenMcaCharges: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const mc = data.mcaCharges;
    if (!mc) return null;
    const byHolder = new Map<string, number>();
    mc.rows.filter((r) => r.status === 'OPEN').forEach((r) => byHolder.set(r.holder, (byHolder.get(r.holder) ?? 0) + r.amountNumeric));
    const donutSlices = Array.from(byHolder.entries()).map(([label, value]) => ({ label, value }));
    const barItems = mc.rows.slice(0, 6).map((r) => ({ label: r.holder, value: r.amountNumeric, valueLabel: r.amount }));
    return (
        <ScreenSection
            kicker="Section · Charges"
            title="MCA charges"
            subtitle="Creditor-wise open & closed charges filed against the company."
            index={index}
        >
            <ScreenStatGrid
                items={[
                    { label: 'Total charges', value: String(mc.rows.length) },
                    { label: 'Open charges', value: String(mc.totalOpen), deltaTone: mc.totalOpen > 0 ? 'neg' : 'pos' },
                    { label: 'Open exposure', value: mc.totalOpenAmountLabel },
                    { label: 'Closed', value: String(mc.totalClosed) },
                ]}
            />
            {donutSlices.length > 0 ? (
                <div style={{ marginTop: 14, padding: 12, background: colors.surfaceAlt, borderRadius: 6 }}>
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: 1,
                            color: colors.muted,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 3,
                        }}
                    >
                        Open exposure by creditor
                    </div>
                    <div style={{ fontSize: 10.5, color: colors.mutedSoft, marginBottom: 8, lineHeight: 1.4 }}>
                        Share of total outstanding (open) charge amount held by each lender.
                    </div>
                    <ScreenDonut slices={donutSlices} size={110} />
                </div>
            ) : null}
            <div style={{ marginTop: 14 }}>
                <div
                    style={{
                        fontSize: 10,
                        letterSpacing: 1,
                        color: colors.muted,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        marginBottom: 6,
                    }}
                >
                    Top charges (by amount)
                </div>
                <ScreenMiniBars items={barItems} />
            </div>
            <div style={{ marginTop: 12 }}>
                {mc.rows.map((r, i) => (
                    <div
                        key={i}
                        style={{
                            padding: '10px 0',
                            borderBottom: `0.5px solid ${colors.hairlineSoft}`,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 2, fontSize: 12.5, fontWeight: 700, color: colors.ink }}>{r.holder}</div>
                            <div style={{ flex: 1, fontSize: 11.5, textAlign: 'right', color: colors.inkSoft, fontWeight: 600 }}>
                                {r.amount}
                            </div>
                            <div style={{ flex: 1, fontSize: 11, textAlign: 'right', color: colors.muted }}>{r.creationDate}</div>
                            <div style={{ width: 70, textAlign: 'right' }}>
                                <ScreenChip tone={statusTone(r.status)}>{r.status}</ScreenChip>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                            {r.rateOfInterest ? <ScreenChip tone="muted">RoI {r.rateOfInterest}</ScreenChip> : null}
                            {r.numberOfHolders && r.numberOfHolders !== '1' ? (
                                <ScreenChip tone="muted">{r.numberOfHolders} holders</ScreenChip>
                            ) : null}
                            {r.modificationDate ? <ScreenChip tone="muted">Mod. {r.modificationDate}</ScreenChip> : null}
                            {r.propertyType && r.propertyType.length <= 32 ? <ScreenChip tone="muted">{r.propertyType}</ScreenChip> : null}
                            {r.instrumentDesc ? <ScreenChip tone="muted">{r.instrumentDesc}</ScreenChip> : null}
                        </div>
                        {r.propertyType && r.propertyType.length > 32 ? (
                            <div
                                style={{
                                    fontSize: 9,
                                    color: colors.ink40,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    lineHeight: 1.4,
                                    marginTop: 6,
                                }}
                            >
                                {r.propertyType}
                            </div>
                        ) : null}
                        {(r.termsOfPayment || r.propertyParticulars || r.extentAndOperation || r.otherTerms) && (
                            // Charge terms render inline + always-expanded (no <details> toggle):
                            // a collapsed <details> only emits its <summary> in the static
                            // server-PDF capture, dropping the terms entirely.
                            <div style={{ marginTop: 6 }}>
                                <div
                                    style={{
                                        fontSize: 10.5,
                                        color: colors.brand,
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Charge terms
                                </div>
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 11.5,
                                        color: colors.inkSoft,
                                        lineHeight: 1.5,
                                        background: colors.surface,
                                        padding: 8,
                                        borderRadius: 4,
                                    }}
                                >
                                    {r.termsOfPayment ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Payment terms:</strong> {r.termsOfPayment}
                                        </div>
                                    ) : null}
                                    {r.propertyParticulars ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Particulars:</strong> {r.propertyParticulars}
                                        </div>
                                    ) : null}
                                    {r.extentAndOperation ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Extent & operation:</strong> {r.extentAndOperation}
                                        </div>
                                    ) : null}
                                    {r.otherTerms ? (
                                        <div>
                                            <strong>Other terms:</strong> {r.otherTerms}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </ScreenSection>
    );
};

export const mcaChargesModule: ModuleDefinition = {
    meta: {
        id: 'mcaCharges' as any,
        title: 'MCA charges',
        description: 'Secured lender exposure, creditor concentration and open / closed status.',
        dataSource: 'polus/insights/tab/data:mcaCharges',
    },
    hasData: (d) => !!d.mcaCharges && d.mcaCharges.rows.length > 0,
    Screen: ScreenMcaCharges,
};
